import {
  type ConsumerMessages,
  DeliverPolicy,
  type JetStreamClient,
  type JsMsg,
  type PushConsumer,
  type StoredMsg,
  jetstream,
} from "@nats-io/jetstream";
import { type KV, Kvm } from "@nats-io/kv";
import { type Msg, type NatsConnection, tokenAuthenticator, wsconnect } from "@nats-io/nats-core";

import type { JobSalaryClient } from "./JobSalary.client";
import {
  type HistoricSalary,
  HistoricSalaryResponseSchema,
  type JobSalaryDto,
  JobSalaryDtoSchema,
  JobTitleToRelatedCareerArraySchema,
  MetroAreaDtoListSchema,
  type WatchlistDto,
  WatchlistDtoListSchema,
  type WatchlistGroup,
  type WatchlistJob,
} from "./JobSalary.dto";
import { JobSalary } from "./JobSalary.model";
import { decompressToParseJson, retry } from "./JobSalary.util";
import type { MetroArea, Quantile } from "./watchlist.filters";

const WATCHLIST_DEFINITIONS = "WatchlistDefinitions";
const METRO_AREA_DEFINITIONS = "MetroAreaDefinitions";

const KV_ALL = "ALL";
const WL_ALL = "All";

const DEFAULT_METRO_AREA: MetroArea = {
  id: "unknown",
  title: "Unknown",
};

const DEFAULT_QUANTILE: Quantile = {
  id: "unknown",
  short: "Unknown",
  title: "Unknown",
};

function toJobSubject(encodedJobTitle: string, metroArea: MetroArea, quantile: string | Quantile): string {
  const quantileId = typeof quantile === "string" ? quantile : quantile.id;
  return `job-salary.${encodedJobTitle}.${metroArea.id}.${quantileId}`;
}

interface SalaryStream {
  streamName: string;
  consumer: PushConsumer;
  consumerMessages: ConsumerMessages;
  subjects: Set<string>;
}

class JobSalaryStreamManager {
  readonly cache: Map<string, SalaryStream> = new Map();

  constructor(readonly jetstreamClient: JetStreamClient) {}

  async subscribe(jobSalary: JobSalary, callback: (msg: JsMsg) => void): Promise<void> {
    const streamName = `JobSalary_${jobSalary.streamName}`;
    const subject = toJobSubject(jobSalary.streamName, jobSalary.metroArea, "*");
    const previousStream = this.cache.get(streamName);
    if (previousStream) {
      if (previousStream.subjects.has(subject)) {
        return;
      }

      await previousStream.consumerMessages.close();
      await previousStream.consumer.delete();

      previousStream.subjects.add(subject);

      const [consumer, consumerMessages] = await this.createConsumer(
        streamName,
        Array.from(previousStream.subjects),
        callback,
      );

      previousStream.consumer = consumer;
      previousStream.consumerMessages = consumerMessages;
    } else {
      const [consumer, consumerMessages] = await this.createConsumer(streamName, [subject], callback);
      this.cache.set(streamName, {
        streamName,
        consumer,
        consumerMessages,
        subjects: new Set<string>().add(subject),
      });
    }
  }

  private async createConsumer(
    streamName: string,
    subjects: string[],
    callback: (msg: JsMsg) => void,
  ): Promise<[PushConsumer, ConsumerMessages]> {
    const consumer = await retry("create consumer", () =>
      this.jetstreamClient.consumers.getPushConsumer(streamName, {
        deliver_policy: DeliverPolicy.LastPerSubject,
        filter_subjects: subjects,
      }),
    );
    const consumerMessages = await consumer.consume({ callback });
    return [consumer, consumerMessages];
  }
}

export class StreamingJobSalaryClient implements JobSalaryClient {
  public static async create(
    natsUrl: string,
    ldDomain: "individuals" | "business",
    clerkToken: string,
  ): Promise<StreamingJobSalaryClient> {
    const natsConnection = await wsconnect({
      servers: natsUrl.split(","),
      authenticator: tokenAuthenticator(`${ldDomain} ${clerkToken}`),
    });

    const kvm = new Kvm(natsConnection);

    return new StreamingJobSalaryClient(
      natsConnection,
      await kvm.open(WATCHLIST_DEFINITIONS),
      await kvm.open(METRO_AREA_DEFINITIONS),
      jetstream(natsConnection),
    );
  }

  watchlistLookupById: Map<string, WatchlistDto> = new Map();

  watchlistGroups: WatchlistGroup[] = [];

  metroAreas: Map<string, MetroArea> = new Map();

  quantiles: Map<string, Quantile> = new Map(
    [
      { id: "a_pct10", short: "<1y" },
      { id: "a_pct25", short: "1-5y" },
      { id: "a_median", short: "5-10y" },
      { id: "a_pct75", short: "10-15y" },
      { id: "a_pct90", short: ">15y" },
    ].map((entry) => [entry.id, { ...entry, title: `${entry.short} exp` }]),
  );

  jobSalaries: Map<string, JobSalary> = new Map();

  started: Promise<void> | null = null;

  allJobEntries: WatchlistJob[] = [];

  jobSalaryStreamManager: JobSalaryStreamManager;

  jobSubjectToWatchlistJob: Map<string, WatchlistJob> = new Map();

  relatedCareersMap: Map<string, string[]> = new Map();

  constructor(
    readonly natsConnection: NatsConnection,
    readonly watchlistDefinitionBucket: KV,
    readonly metroAreaBucket: KV,
    readonly jetstreamClient: JetStreamClient,
  ) {
    this.jobSalaryStreamManager = new JobSalaryStreamManager(jetstreamClient);
  }

  async start(): Promise<void> {
    if (this.started) {
      return this.started;
    }

    this.started = this.load();
    return this.started;
  }

  static groupWatchlists(
    allJobEntries: WatchlistJob[],
    watchlists: WatchlistDto[],
    indexById: Map<string, WatchlistDto>,
  ): WatchlistGroup[] {
    const groupByGroupNameLookup = new Map<string, WatchlistDto[]>();
    const groups: WatchlistGroup[] = [];

    const uniqueTitles = new Set<string>();

    for (const watchlist of watchlists) {
      indexById.set(watchlist.id, watchlist);

      for (const entry of watchlist.entries) {
        if (!uniqueTitles.has(entry.jobTitle)) {
          uniqueTitles.add(entry.jobTitle);
          allJobEntries.push(entry);
        }
      }

      const { groupName } = watchlist;
      const previousGrouping = groupByGroupNameLookup.get(groupName);
      if (previousGrouping) {
        previousGrouping.push(watchlist);
      } else {
        const newWatchlists: WatchlistDto[] = [watchlist];
        groupByGroupNameLookup.set(groupName, newWatchlists);
        groups.push({
          id: groupName,
          title: groupName,
          watchlists: newWatchlists,
        });
      }
    }

    groups.sort((a, b) => a.title.localeCompare(b.title));

    const allWatchlist: WatchlistDto = {
      id: WL_ALL,
      title: WL_ALL,
      groupName: WL_ALL,
      entries: allJobEntries,
    };

    indexById.set(allWatchlist.id, allWatchlist);

    groups.push({
      id: WL_ALL,
      title: WL_ALL,
      watchlists: [allWatchlist],
    });

    return groups;
  }

  private async load(): Promise<void> {
    const relatedCareersEntry = await this.watchlistDefinitionBucket.get("RELATED_CAREERS");
    if (relatedCareersEntry) {
      const relatedCareersArray = await decompressToParseJson(
        relatedCareersEntry.value,
        JobTitleToRelatedCareerArraySchema.parse,
      );

      relatedCareersArray.forEach((career) => {
        this.relatedCareersMap.set(career.jobTitle, career.relatedCareers);
      });
    }

    const watchlistEntries = await this.watchlistDefinitionBucket.get(KV_ALL);
    if (watchlistEntries) {
      const watchlists = await decompressToParseJson(watchlistEntries.value, WatchlistDtoListSchema.parse);

      this.watchlistGroups = StreamingJobSalaryClient.groupWatchlists(
        this.allJobEntries,
        watchlists,
        this.watchlistLookupById,
      );

      watchlists.forEach((watchlist) => {
        watchlist.entries.forEach((entry) => {
          this.jobSubjectToWatchlistJob.set(entry.jobSubject, entry);
        });
      });
    }

    const metroAreaEntries = await this.metroAreaBucket.get(KV_ALL);
    if (metroAreaEntries) {
      const metroAreas = MetroAreaDtoListSchema.parse(metroAreaEntries.json());
      for (const metroArea of metroAreas) {
        this.metroAreas.set(metroArea.areaId, { id: metroArea.areaId, title: metroArea.areaTitle });
      }
    }
  }

  getWatchlistGroups(): WatchlistGroup[] {
    return Array.from(this.watchlistGroups);
  }

  getMetroAreas(): MetroArea[] {
    // oxlint-disable-next-line typescript/require-array-sort-compare
    return Array.from(this.metroAreas.values()).sort();
  }

  getQuantiles(): Quantile[] {
    return Array.from(this.quantiles.values());
  }

  getRelatedJobSalaries(jobSalary: JobSalary): JobSalary[] {
    const relatedCareers = this.relatedCareersMap.get(jobSalary.streamName);
    if (!relatedCareers) return [];

    return relatedCareers.map((relatedTitle) =>
      this.getOrCreateJobSalary(relatedTitle, jobSalary.metroArea, jobSalary.quantile),
    );
  }

  getJobSalaries(
    watchlistId: string,
    metroAreaId: string,
    quantileId: string,
  ): { watchlist: WatchlistDto; jobSalaries: JobSalary[] } | null {
    const watchlist = this.watchlistLookupById.get(watchlistId);
    if (!watchlist) {
      return null;
    }

    const metroArea = this.metroAreas.get(metroAreaId);
    if (!metroArea) {
      return null;
    }

    const quantile = this.quantiles.get(quantileId);
    if (!quantile) {
      return null;
    }

    const salaries = watchlist.entries.map((entry) => this.getOrCreateJobSalary(entry.jobSubject, metroArea, quantile));

    return { watchlist, jobSalaries: salaries };
  }

  getOrCreateJobSalary(jobSubject: string, metroArea: MetroArea, quantile: Quantile): JobSalary {
    const id = `job-salary.${jobSubject}.${metroArea.id}.${quantile.id}`;
    const jobSalary = this.jobSalaries.get(id);
    if (jobSalary) {
      return jobSalary;
    }
    const watchlistJob = this.jobSubjectToWatchlistJob.get(jobSubject);
    const newJobSalary = new JobSalary(
      jobSubject,
      id,
      watchlistJob?.jobTitle ?? jobSubject,
      watchlistJob?.jobTitleAliases ?? [],
      metroArea,
      quantile,
      (salary) => this.subscribeToJobSalary(salary),
    );
    this.jobSalaries.set(id, newJobSalary);
    return newJobSalary;
  }

  getJobSalariesForAllQuantiles(jobSalaryId: string): JobSalary[] {
    const parts = jobSalaryId.split(".");
    const jobSubject = parts[1];
    const metroAreaId = parts[2];
    const metroArea = this.metroAreas.get(metroAreaId) ?? DEFAULT_METRO_AREA;

    return this.getQuantiles().map((quantile) => {
      const jobSalary = this.getOrCreateJobSalary(jobSubject, metroArea, quantile);
      jobSalary.subscribe();
      return jobSalary;
    });
  }

  private messageCallback(msg: JsMsg) {
    const salary = this.jobSalaries.get(msg.subject);
    if (salary) {
      handleMessage(null, msg, salary);
    } else {
      // eslint-disable-next-line unused-imports/no-unused-vars,@typescript-eslint/naming-convention
      const [_jobSalaryPrefix, jobSubject, metroAreaId, quantileId] = msg.subject.split(".");
      const watchlistJob = this.jobSubjectToWatchlistJob.get(jobSubject);
      const newJobSalary = new JobSalary(
        msg.info.stream,
        msg.subject,
        watchlistJob?.jobTitle ?? jobSubject,
        watchlistJob?.jobTitleAliases ?? [],
        this.metroAreas.get(metroAreaId) ?? DEFAULT_METRO_AREA,
        this.quantiles.get(quantileId) ?? DEFAULT_QUANTILE,
        null,
      );
      this.jobSalaries.set(msg.subject, newJobSalary);
      handleMessage(null, msg, newJobSalary);
    }
  }

  async subscribeToJobSalary(jobSalary: JobSalary): Promise<void> {
    await this.jobSalaryStreamManager.subscribe(jobSalary, (msg: JsMsg) => this.messageCallback(msg));
  }

  async fetchHistoricSalaryData(jobSalary: JobSalary, type: "ytd" | "five-year"): Promise<HistoricSalary[]> {
    const request = {
      jobTitle: jobSalary.title,
      areaId: jobSalary.metroArea.id,
      quantileId: jobSalary.quantile.id,
      period: type,
    };

    // Set a 10-second timeout for the NATS request
    // Default NATS timeout is 1 second, which may be too short for historical data
    const response = await this.natsConnection.request("historic-salaries", JSON.stringify(request), {
      timeout: 10000, // 10 seconds
    });

    const parsedResponseResult = HistoricSalaryResponseSchema.safeParse(response.json());
    if (parsedResponseResult.error) {
      return [];
    }
    if (parsedResponseResult.data.status === "error") {
      return [];
    }

    return parsedResponseResult.data.data;
  }
}

function handleMessage(err: Error | null, msg: StoredMsg | Msg | JsMsg, jobSalary: JobSalary): void {
  if (err) {
    return;
  }

  const data = msg.json();
  const zodResult = JobSalaryDtoSchema.safeParse(data);
  if (zodResult.success) {
    updateSalaryState(jobSalary, zodResult.data);
  }
}

function updateSalaryState(state: JobSalary, publicSalary: JobSalaryDto): void {
  state.salary.value = publicSalary.salary;
  state.salaryUp.value = publicSalary.salaryUp;
  state.salaryDown.value = publicSalary.salaryDown;

  state.netChange.value = publicSalary.netChange;
  state.netChangeUp.value = publicSalary.netChangeUp;
  state.netChangeDown.value = publicSalary.netChangeDown;

  state.totalCostProjectionOneYear.value = publicSalary.totalCostProjectionOneYear;
  state.totalCostProjectionThreeYears.value = publicSalary.totalCostProjectionThreeYears;
  state.totalCostProjectionFiveYears.value = publicSalary.totalCostProjectionFiveYears;

  state.liquidity.value = publicSalary.liquidity;
  state.averageTenureInYears.value = publicSalary.averageTenureInYears;

  state.growthRate.value = publicSalary.growthRate;
  state.growthRateUp.value = publicSalary.growthRate > 0;
  state.growthRateDown.value = publicSalary.growthRate < 0;

  state.netChangeOneYear.value = publicSalary.netChangeOneYear;
  state.netChangeTwoYear.value = publicSalary.netChangeTwoYear;
  state.netChangeThreeYear.value = publicSalary.netChangeThreeYear;
  state.netChangeFourYear.value = publicSalary.netChangeFourYear;
  state.netChangeFiveYear.value = publicSalary.netChangeFiveYear;
}
